/**
 * Vista previa local y detección de cambios pendientes en el modal de edición de reserva.
 * El total oficial sigue viniendo del servidor hasta pulsar «Aplicar».
 */

import { reconcileVehicleLinePrices } from './bookingPricingDisplay.js';

function normalizeDeliveryType(v) {
  if (v === 'HOTEL') return 'DELIVERY';
  return v;
}

export function sumVehicleSubtotalsFromBooking(booking) {
  return (
    Math.round(
      (booking?.vehicleRentals || []).reduce((s, r) => s + (Number(r.subtotal) || 0), 0) * 100
    ) / 100
  );
}

export function sumDraftAccessoryPrices(accessoryDraftByRentalId) {
  return Math.round(
    Object.values(accessoryDraftByRentalId || {}).reduce((s, arr) => {
      if (!Array.isArray(arr)) return s;
      return s + arr.reduce((a, x) => a + (Number(x.price) || 0), 0);
    }, 0) * 100
  ) / 100;
}

export function sumSavedAccessoryPrices(booking) {
  return (
    Math.round(
      (booking?.vehicleRentals || []).reduce(
        (s, vr) =>
          s +
          (vr.accessories || []).reduce((a, x) => a + (Number(x.finalPrice) || 0), 0),
        0
      ) * 100
    ) / 100
  );
}

/**
 * Desglose estimado con borradores de accesorios y entrega (sin llamar al servidor).
 */
export function computeDraftPriceBreakdown(booking, editForm, accessoryDraftByRentalId) {
  const vehicles = sumVehicleSubtotalsFromBooking(booking);
  const accessories = sumDraftAccessoryPrices(accessoryDraftByRentalId);
  const isDel =
    normalizeDeliveryType(editForm?.deliveryType) === 'DELIVERY';
  const dcRaw = parseFloat(String(editForm?.deliveryCost ?? '0').replace(',', '.'));
  const delivery = isDel && Number.isFinite(dcRaw) && dcRaw >= 0 ? Math.round(dcRaw * 100) / 100 : 0;
  const discount = Number(booking?.discount) || 0;
  const totalPreview = Math.round((vehicles + accessories + delivery - discount) * 100) / 100;
  return { vehicles, accessories, delivery, discount, totalPreview };
}

/**
 * Detecta borrador económico vs servidor y líneas de incremento para el desglose de «Cobrar saldo pendiente».
 * El endpoint pending-supplement-preview solo conoce datos guardados; si hay accesorios/entrega sin aplicar,
 * hay que mostrar localmente el sobrecoste (p. ej. nuevos accesorios añadidos en el borrador).
 */
export function getPendingSupplementDraftExtraLines(booking, editForm, accessoryDraftByRentalId) {
  const savedAcc = sumSavedAccessoryPrices(booking);
  const draftAcc = sumDraftAccessoryPrices(accessoryDraftByRentalId);
  const dAcc = roundMoney(draftAcc - savedAcc);
  const savedDel = effectiveSavedDeliveryEur(booking);
  const draftDel = effectiveDraftDeliveryEur(editForm);
  const dDel = roundMoney(draftDel - savedDel);
  const eps = 0.02;
  const lines = [];
  if (dAcc > eps) {
    lines.push({ concept: 'Accesorios', amount: dAcc });
  }
  if (dDel > eps) {
    lines.push({ concept: 'Suplemento entrega', amount: dDel });
  }
  const hasUnsavedEconomicDraft = Math.abs(dAcc) > eps || Math.abs(dDel) > eps;
  return { lines, hasUnsavedEconomicDraft, dAcc, dDel };
}

function rentalLabelForCobrarSaldo(vr) {
  const sn = vr?.vehicle?.serialNumber;
  if (sn) return String(sn).trim();
  return `línea #${vr.id}`;
}

/**
 * Una fila de alquiler por bicicleta: importe = subtotal de línea; si el total agregado (vehiclesTargetTotal)
 * no coincide con la suma de subtotales (p. ej. línea nueva con subtotal 0), reparte la diferencia en líneas en 0
 * o escala proporcionalmente.
 */
function perRentalVehicleRows(booking, vehiclesTargetTotal) {
  const rentals = booking?.vehicleRentals || [];
  const target = roundMoney(Number(vehiclesTargetTotal) || 0);
  if (!rentals.length || target <= 0.005) return [];
  let amounts = rentals.map((vr) => roundMoney(Number(vr.subtotal) || 0));
  let sumA = roundMoney(amounts.reduce((a, b) => a + b, 0));
  const extra = roundMoney(target - sumA);
  if (extra > 0.02) {
    const zeroIdx = amounts.map((a, i) => i).filter((i) => amounts[i] <= 0.005);
    if (zeroIdx.length > 0) {
      const per = roundMoney(extra / zeroIdx.length);
      zeroIdx.forEach((i, k) => {
        amounts[i] =
          k === zeroIdx.length - 1
            ? roundMoney(extra - per * (zeroIdx.length - 1))
            : roundMoney(amounts[i] + per);
      });
    } else {
      amounts = amounts.map((x) => roundMoney((x / sumA) * target));
    }
  } else if (extra < -0.02 && sumA > 0.005) {
    amounts = amounts.map((x) => roundMoney((x / sumA) * target));
  }
  sumA = roundMoney(amounts.reduce((a, b) => a + b, 0));
  const drift = roundMoney(target - sumA);
  if (Math.abs(drift) > 0.005 && amounts.length) {
    amounts[amounts.length - 1] = roundMoney(amounts[amounts.length - 1] + drift);
  }
  return rentals.map((vr, i) => ({
    concept: `Alquiler (${rentalLabelForCobrarSaldo(vr)})`,
    amount: amounts[i],
  }));
}

/**
 * Parte el borrador de accesorios en no-seguro / seguro usando categoría (reserva guardada o catálogo del modelo).
 */
function splitDraftAccessoriesByInsurance(
  booking,
  accessoryDraftByRentalId,
  getAccessoryCategory
) {
  const rentals = booking?.vehicleRentals || [];
  let acc = 0;
  let ins = 0;
  for (const vr of rentals) {
    for (const row of accessoryDraftByRentalId?.[vr.id] || []) {
      const price = Number(row.price) || 0;
      let cat = getAccessoryCategory?.(vr.id, row.accessoryId);
      if (cat == null) {
        const saved = (vr.accessories || []).find((a) => a.accessoryId === row.accessoryId);
        cat = saved?.accessory?.category;
      }
      const isIns = cat === 'INSURANCE' || String(cat || '').toUpperCase() === 'INSURANCE';
      if (isIns) ins += price;
      else acc += price;
    }
  }
  return { accessoriesOnly: roundMoney(acc), insurance: roundMoney(ins) };
}

/**
 * Tabla de desglose para «Cobrar saldo pendiente» con borrador sin aplicar: una línea de alquiler por bicicleta
 * (importe alineado al subtotal de esa línea), accesorios y seguros separados si cuadran con el total del borrador.
 * `getAccessoryCategory(rentalId, accessoryId)` opcional (p. ej. catálogo por modelo en el admin).
 */
export function computeCobrarSaldoPendingDraftTable(
  booking,
  editForm,
  accessoryDraftByRentalId,
  paidTotal,
  getAccessoryCategory
) {
  const b = computeDraftPriceBreakdown(booking, editForm, accessoryDraftByRentalId);
  const paid = roundMoney(Number(paidTotal) || 0);
  const pendienteEstimado = roundMoney(Math.max(0, b.totalPreview - paid));
  const eps = 0.005;
  const rows = [];

  const vehicleRows = perRentalVehicleRows(booking, b.vehicles);
  for (const r of vehicleRows) {
    if (r.amount > eps) rows.push(r);
  }

  const { accessoriesOnly, insurance } = splitDraftAccessoriesByInsurance(
    booking,
    accessoryDraftByRentalId,
    getAccessoryCategory
  );
  const splitSum = roundMoney(accessoriesOnly + insurance);
  if (Math.abs(splitSum - b.accessories) <= 0.05) {
    // Mostrar siempre el total agregado de accesorios (incluye seguros), y opcionalmente el split.
    if (b.accessories > eps) rows.push({ concept: 'Accesorios (total)', amount: b.accessories });
    if (accessoriesOnly > eps) rows.push({ concept: 'Accesorios (sin seguros)', amount: accessoriesOnly });
    if (insurance > eps) rows.push({ concept: 'Seguros', amount: insurance });
  } else if (b.accessories > eps) {
    rows.push({ concept: 'Accesorios y seguros', amount: b.accessories });
  }

  if (b.delivery > eps) {
    rows.push({ concept: 'Entrega a domicilio', amount: b.delivery });
  }
  if (b.discount > eps) {
    rows.push({ concept: 'Descuento', amount: -b.discount });
  }
  rows.push({ concept: 'Total reserva (estimado, IVA incl.)', amount: b.totalPreview });
  rows.push({ concept: 'Ya cobrado', amount: -paid });
  return { rows, pendienteEstimado, totalPreview: b.totalPreview, paid };
}

/**
 * Incrementos de importe en accesorios (borrador vs guardado), separados seguros / resto.
 */
export function computeAccessoryDraftDeltaSplit(booking, accessoryDraftByRentalId, getAccessoryCategory) {
  const eps = 0.005;
  let insuranceDelta = 0;
  let accessoriesDelta = 0;
  for (const vr of booking?.vehicleRentals || []) {
    const savedById = new Map(
      (vr.accessories || []).map((a) => [a.accessoryId, roundMoney(Number(a.finalPrice) || 0)])
    );
    for (const row of accessoryDraftByRentalId?.[vr.id] || []) {
      const draftP = roundMoney(Number(row.price) || 0);
      const savedP = savedById.has(row.accessoryId) ? savedById.get(row.accessoryId) : 0;
      const delta = roundMoney(draftP - savedP);
      if (delta <= eps) continue;
      let cat = getAccessoryCategory?.(vr.id, row.accessoryId);
      if (cat == null) {
        const saved = (vr.accessories || []).find((a) => a.accessoryId === row.accessoryId);
        cat = saved?.accessory?.category;
      }
      const isIns = cat === 'INSURANCE' || String(cat || '').toUpperCase() === 'INSURANCE';
      if (isIns) insuranceDelta += delta;
      else accessoriesDelta += delta;
    }
  }
  return {
    insuranceDelta: roundMoney(insuranceDelta),
    accessoriesDelta: roundMoney(accessoriesDelta),
  };
}

/**
 * Líneas orientativas para la factura Quipu de **suplemento** (solo incrementos vs datos guardados),
 * mismos conceptos y orden que el backend: Entrega → Alquiler → Seguros → Accesorios.
 * El resto del importe objetivo se imputa a alquiler (p. ej. bici nueva o saldo de alquiler pendiente).
 */
export function computeQuipuSupplementOrientativeLines(
  booking,
  editForm,
  accessoryDraftByRentalId,
  amountTarget,
  getAccessoryCategory
) {
  const target = roundMoney(Math.max(0, Number(amountTarget) || 0));
  if (target <= 0.02) return { lines: [], totalEur: 0 };
  const { dDel } = getPendingSupplementDraftExtraLines(booking, editForm, accessoryDraftByRentalId);
  const deliveryLine = roundMoney(Math.max(0, dDel));
  const { insuranceDelta, accessoriesDelta } = computeAccessoryDraftDeltaSplit(
    booking,
    accessoryDraftByRentalId,
    getAccessoryCategory
  );
  const prelim = roundMoney(deliveryLine + insuranceDelta + accessoriesDelta);
  let vehiclePart = roundMoney(target - prelim);
  if (vehiclePart < 0) vehiclePart = 0;

  const lines = [];
  if (deliveryLine > 0.005) {
    lines.push({ concept: 'Entrega a domicilio', amount: deliveryLine });
  }
  if (vehiclePart > 0.005) {
    lines.push({ concept: 'Alquiler de bicicletas', amount: vehiclePart });
  }
  if (insuranceDelta > 0.005) {
    lines.push({ concept: 'Seguros', amount: insuranceDelta });
  }
  if (accessoriesDelta > 0.005) {
    lines.push({ concept: 'Accesorios', amount: accessoriesDelta });
  }

  const sum = roundMoney(lines.reduce((s, l) => s + l.amount, 0));
  const drift = roundMoney(target - sum);
  if (Math.abs(drift) > 0.005 && lines.length > 0) {
    lines[lines.length - 1].amount = roundMoney(lines[lines.length - 1].amount + drift);
  } else if (Math.abs(drift) > 0.005 && lines.length === 0) {
    lines.push({ concept: 'Alquiler de bicicletas', amount: target });
  }
  return { lines, totalEur: target };
}

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function effectiveSavedDeliveryEur(booking) {
  const t = normalizeDeliveryType(booking?.deliveryType);
  if (t !== 'DELIVERY') return 0;
  return roundMoney(parseFloat(booking?.deliveryCost) || 0);
}

function effectiveDraftDeliveryEur(editForm) {
  const t = normalizeDeliveryType(editForm?.deliveryType);
  if (t !== 'DELIVERY') return 0;
  const dcRaw = parseFloat(String(editForm?.deliveryCost ?? '0').replace(',', '.'));
  return Number.isFinite(dcRaw) && dcRaw >= 0 ? roundMoney(dcRaw) : 0;
}

function rentalDisplayLabel(vr) {
  const sn = vr?.vehicle?.serialNumber;
  if (sn) return String(sn).trim();
  return `línea #${vr.id}`;
}

/**
 * Desglose de lo que deja de cobrarse al pasar del guardado al borrador (bajadas en accesorios/entrega).
 * Sirve para validar el abono estimado en la pestaña Cobros antes de aplicar.
 * @returns {{ lines: Array<{ key: string, description: string, amountEur: number }> }}
 */
export function computeDraftCreditDownLines(booking, editForm, accessoryDraftByRentalId) {
  const lines = [];
  const rentals = booking?.vehicleRentals || [];

  for (const vr of rentals) {
    const saved = vr.accessories || [];
    const draftRows = accessoryDraftByRentalId?.[vr.id] || [];
    const draftById = new Map(draftRows.map((r) => [r.accessoryId, roundMoney(r.price)]));
    const bike = rentalDisplayLabel(vr);

    for (const sa of saved) {
      const aid = sa.accessoryId;
      const savedPrice = roundMoney(sa.finalPrice);
      const name = (sa.accessory && sa.accessory.name) || `Accesorio #${aid}`;
      const draftPrice = draftById.has(aid) ? draftById.get(aid) : null;

      if (draftPrice === null) {
        lines.push({
          key: `removed-${vr.id}-${aid}`,
          description: `Quitado: ${name} (bicicleta ${bike})`,
          amountEur: savedPrice,
        });
      } else if (draftPrice + 0.02 < savedPrice) {
        lines.push({
          key: `reduced-${vr.id}-${aid}`,
          description: `Menor importe: ${name} (bicicleta ${bike})`,
          amountEur: roundMoney(savedPrice - draftPrice),
        });
      }
    }
  }

  const savedDel = effectiveSavedDeliveryEur(booking);
  const draftDel = effectiveDraftDeliveryEur(editForm);
  if (savedDel - draftDel > 0.02) {
    lines.push({
      key: 'delivery-down',
      description:
        draftDel < 0.02
          ? 'Suplemento entrega: eliminado (recogida en tienda o 0 €)'
          : 'Suplemento entrega: importe reducido',
      amountEur: roundMoney(savedDel - draftDel),
    });
  }

  return { lines };
}

export function isAccessoryDraftDirty(booking, accessoryDraftByRentalId) {
  const rentals = booking?.vehicleRentals || [];
  for (const vr of rentals) {
    const saved = (vr.accessories || [])
      .map((a) => ({ accessoryId: a.accessoryId, price: Math.round((Number(a.finalPrice) || 0) * 100) / 100 }))
      .sort((x, y) => x.accessoryId - y.accessoryId);
    const draft = (accessoryDraftByRentalId?.[vr.id] || [])
      .map((x) => ({ accessoryId: x.accessoryId, price: Math.round((Number(x.price) || 0) * 100) / 100 }))
      .sort((x, y) => x.accessoryId - y.accessoryId);
    if (saved.length !== draft.length) return true;
    for (let i = 0; i < saved.length; i++) {
      if (saved[i].accessoryId !== draft[i].accessoryId) return true;
      if (Math.abs(saved[i].price - draft[i].price) > 0.02) return true;
    }
  }
  return false;
}

export function isDeliveryFormDirty(booking, editForm) {
  if (!booking || !editForm) return false;
  const bt = normalizeDeliveryType(booking.deliveryType);
  const et = normalizeDeliveryType(editForm.deliveryType);
  if (bt !== et) return true;
  const costB = Math.round((Number(booking.deliveryCost) || 0) * 100) / 100;
  const ec = parseFloat(String(editForm.deliveryCost || '0').replace(',', '.'));
  const costE = Number.isFinite(ec) && ec >= 0 ? Math.round(ec * 100) / 100 : 0;
  if (Math.abs(costB - costE) > 0.02) return true;
  const addrB = (booking.hotelAddress || '').trim();
  const addrE = (editForm.hotelAddress || '').trim();
  if (addrB !== addrE) return true;
  return false;
}

export function isScheduleDraftDirty(booking, scheduleDraftStart, scheduleDraftEnd) {
  if (!booking?.startDateTime || !booking?.endDateTime) return false;
  if (!scheduleDraftStart?.trim() || !scheduleDraftEnd?.trim()) return false;
  const toMs = (localStr) => {
    const d = new Date(localStr);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  };
  const a = toMs(scheduleDraftStart);
  const b = toMs(scheduleDraftEnd);
  if (a == null || b == null) return false;
  const tol = 90000;
  return (
    Math.abs(a - new Date(booking.startDateTime).getTime()) > tol ||
    Math.abs(b - new Date(booking.endDateTime).getTime()) > tol
  );
}

/**
 * Desglose de precios según datos guardados en servidor (misma lógica que el detalle «Ver reserva»).
 */
export function getOfficialBookingPriceBreakdown(booking) {
  const rentals = booking?.vehicleRentals || [];
  const { poolVehicles } = reconcileVehicleLinePrices(booking, rentals);
  const sumLineSubtotals =
    Math.round(rentals.reduce((s, r) => s + (Number(r.subtotal) || 0), 0) * 100) / 100;
  const storedRaw = booking?.vehiclesTotal;
  const storedVtNum = storedRaw != null && storedRaw !== '' ? Number(storedRaw) : NaN;
  const vehiclesTotal =
    sumLineSubtotals > 0
      ? sumLineSubtotals
      : Number.isFinite(storedVtNum) && !Number.isNaN(storedVtNum)
        ? Math.round(storedVtNum * 100) / 100
        : poolVehicles;

  const calculatedAccessoriesTotal =
    rentals.reduce((sum, rental) => {
      const rentalAccessories = rental.accessories || [];
      return (
        sum +
        rentalAccessories.reduce((accSum, acc) => accSum + (parseFloat(acc.finalPrice) || 0), 0)
      );
    }, 0) || 0;

  const insuranceFromLines =
    rentals.reduce((sum, rental) => {
      const rentalAccessories = rental.accessories || [];
      return (
        sum +
        rentalAccessories.reduce((accSum, acc) => {
          const cat = acc.accessory?.category;
          const isIns = cat === 'INSURANCE' || String(cat).toUpperCase() === 'INSURANCE';
          return isIns ? accSum + (parseFloat(acc.finalPrice) || 0) : accSum;
        }, 0)
      );
    }, 0) || 0;

  const hasRentalAccessoryRows = rentals.some((r) => (r.accessories || []).length > 0);
  const useLineSplit = hasRentalAccessoryRows;

  const calculatedInsuranceTotal = useLineSplit
    ? insuranceFromLines
    : booking?.insuranceTotal != null && booking.insuranceTotal > 0
      ? parseFloat(booking.insuranceTotal)
      : insuranceFromLines;

  const accessoriesOnlyTotal = useLineSplit
    ? calculatedAccessoriesTotal - calculatedInsuranceTotal
    : Math.max(
        0,
        (parseFloat(booking.accessoriesTotal) || 0) - calculatedInsuranceTotal
      );

  const deliveryCost = parseFloat(booking.deliveryCost) || 0;
  const discount = parseFloat(booking.discount) || 0;
  const dt = booking?.deliveryType;
  const showDelivery = dt === 'DELIVERY' || dt === 'HOTEL';

  return {
    vehiclesTotal,
    accessoriesOnlyTotal,
    insuranceTotal: calculatedInsuranceTotal,
    deliveryCost,
    showDelivery,
    discount,
    total: Number(booking?.total) || 0,
  };
}
